import React, { useEffect, useState } from 'react';
import ReceptionV from './Reception.v';
import ReceptionModal from '../_common/modal/ReceptionModal';
import PageUtil from 'utils/Page.util';
import moment from 'moment';
import { withRouter } from 'core/withRouter';
import { dateFormat } from 'utils/Date.util';
import { ReceptionDTO, ReceptionSearchDTO } from 'models/reception';
import receptionService from 'services/reception.service';
import kpiService from 'services/kpi.service';
import { DateType, getReceptionStatus, ReceptionStatus } from 'enums/reception.enum';
import { ReceptionKpiDTO } from 'models/kpi';

/** 내부 상태 모델 */
class Params {
    error: string | null = null;
    isLoaded = false;
    total: number = 0;

    datePickerMode: 'daily' | 'monthly' = 'daily';

    list: ReceptionDTO[] = [];
    search: ReceptionSearchDTO = new ReceptionSearchDTO();

    kpiLoaded = false;
    kpiData: ReceptionKpiDTO | null = null;

    statusSet = getReceptionStatus();
    typeSet = ['일반', '맞교환', '출장', '보상판매', '방문', '판매'];
}

function Reception() {
    const [params, setParams] = useState(new Params());
    const [search, setSearch] = useState(false);

    // 모달 상태 관리 추가
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
    const [editingRow, setEditingRow] = useState<ReceptionDTO | null>(null);

    /** 초기 진입 시 기본 검색기간(최근 30일) 설정 + 조회 */
    useEffect(() => {
        const cur = PageUtil.deepCopy(params) as Params;
        cur.search.dateType = DateType.RECEIVED;
        // cur.search.startDate = dateFormat(new Date(new Date().setDate(new Date().getDate() - 30)), 'YYYY-MM-DD');
        // cur.search.endDate = dateFormat(new Date(), 'YYYY-MM-DD');
        // cur.search.ignoreStatus = [ReceptionStatus.DONE, ReceptionStatus.PAID, ReceptionStatus.CONSULTED].join(',');
        cur.search.startDate = null;
        cur.search.endDate = null;
        cur.isLoaded = true;
        setParams(cur);
        // setSearch(true);

        loadKPIData();
    }, []);

    /** 검색 트리거 */
    useEffect(() => {
        if (!search) return;
        setSearch(false);
        getList(params.search);
    }, [search]);

    /** 목록 조회 */
    async function getList(search: ReceptionSearchDTO) {
        try {
            // if (search.startDate && search.endDate && !search.dateType) search.dateType = DateType.RECEIVED;

            console.log("search", search);

            const res = await receptionService.list(search);
            console.log("res", res);
            // res = { content: ReceptionDTO[], totalElements: number }
            setParams((prev) => ({
                ...prev,
                list: res.content || [],
                total: res.totalElements || 0,
                isLoaded: true,
                error: null,
            }));
        } catch (error) {
            setParams((prev) => ({
                ...prev,
                isLoaded: true,
                error: error as any,
            }));
            PageUtil.handleApiError(error);
        }
    }

    /** KPI 데이터 조회 */
    async function loadKPIData() {
        try {
            const kpiData = await kpiService.reception();
            console.log("kpiData", kpiData);
            setParams(prev => ({
                ...prev,
                kpiLoaded: true,
                kpiData,
            }));
        } catch (error) {
            console.error('KPI 데이터 조회 실패:', error);
            setParams(prev => ({
                ...prev,
                kpiLoaded: true,
            }));
        }
    }

    /** 검색 버튼 */
    function onSearch() {
        const cur = PageUtil.deepCopy(params) as Params;
        cur.isLoaded = false;
        cur.search.page = 1; // 첫 페이지로
        setParams(cur);
        setSearch(true);
    }

    /** 초기화 */
    function onClearFilter() {
        const cur = PageUtil.deepCopy(params) as Params;
        cur.search.dateType = null;
        cur.search.types = "";
        // cur.search.keyword = '';
        cur.search.startDate = null;
        cur.search.endDate = null;
        cur.search.page = 1;
        cur.isLoaded = false;
        setParams(cur);
        setSearch(true);
    }

    function onClearManufactureMonthFilter() {
        const cur = PageUtil.deepCopy(params) as Params;
        cur.search.startManufactureMonth = null;
        cur.search.endManufactureMonth = null;
        cur.isLoaded = false;
        cur.search.page = 1;
        setParams(cur);
        setSearch(true);
    }

    /** Today 버튼 - 당일 데이터만 조회 */
    function onTodaySearch() {
        const cur = PageUtil.deepCopy(params) as Params;
        const today = dateFormat(new Date(), 'YYYY-MM-DD');

        cur.search.startDate = today;
        cur.search.endDate = today;
        cur.isLoaded = false;
        cur.search.page = 1; // 첫 페이지로

        setParams(cur);
        setSearch(true);
    }

    /** DAY/MONTH 토글 */
    function onToggleDatePickerMode(mode: 'daily' | 'monthly') {
        const cur = PageUtil.deepCopy(params) as Params;
        cur.datePickerMode = mode;
        setParams(cur);
    }

    /** 날짜 변경 핸들러 */
    function onHandleSearchDate(value: string, col: 'startDate' | 'endDate') {
        const cur = PageUtil.deepCopy(params) as Params;

        if (params.datePickerMode === 'monthly') {
            if (col === 'startDate') {
                cur.search[col] = moment(value, 'YYYY-MM').startOf('month').format('YYYY-MM-DD');
            } else if (col === 'endDate') {
                cur.search[col] = moment(value, 'YYYY-MM').endOf('month').format('YYYY-MM-DD');
            }
        } else {
            cur.search[col] = value;
        }

        setParams(cur);
    }

    /** 행 상태 변경 → 즉시 저장(간단 예시) */
    async function onChangeStatus(row: ReceptionDTO, newStatus: ReceptionStatus) {
        if (!confirm("처리상황을 변경하시겠습니까?")) return;
        try {
            await receptionService.updateStatus({ id: row.id, status: newStatus });
            onSearch();
        } catch (error) {
            PageUtil.handleApiError(error);
        }
    }

    /** 신규 접수 모달 열기 */
    function onOpenCreate() {
        setModalMode('create');
        setEditingRow(null);
        setShowModal(true);
    }

    /** 수정 모달 열기 */
    function onOpenEdit(row: ReceptionDTO) {
        setModalMode('edit');
        setEditingRow(row);
        setShowModal(true);
    }

    /** 모달에서 저장 완료 시 */
    async function onModalSave(reception: ReceptionDTO, close: boolean = true) {
        try {
            console.log("reception", reception);
            await receptionService.save(reception);
            if (close) setShowModal(false);
            setSearch(true); // 목록 새로고침
            alert('저장되었습니다.');
            // 필요시 목록 새로고침 등 추가 처리
        } catch (error) {
            console.error('접수 저장 실패:', error);
            alert('저장에 실패했습니다.');
        }
    }

    /** 모달 닫기 */
    function onModalClose() {
        setShowModal(false);
        setEditingRow(null);
    }

    const args = {
        params,
        setParams,
        setSearch,

        handleChange: PageUtil.handleChange,
        onSearch,
        onClearFilter,
        onClearManufactureMonthFilter,
        onTodaySearch, // Today 버튼 핸들러 추가
        onToggleDatePickerMode,
        onHandleSearchDate,

        onOpenCreate,
        onOpenEdit,

        onChangeStatus,
    };

    return (
        <>
            <ReceptionV {...args} />

            {/* 접수 등록/수정 모달 */}
            <ReceptionModal
                show={showModal}
                title={modalMode === 'create' ? '신규 접수 등록' : '접수 수정'}
                initial={editingRow}
                onSave={onModalSave}
                onClose={onModalClose}
            />
        </>
    );
}

export default withRouter(Reception);